import { useState, useMemo, forwardRef, useImperativeHandle, useRef } from "react";
import { motion } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ScatterChart, Scatter, ResponsiveContainer, Cell,
  LineChart, Line, ReferenceLine, ZAxis, ReferenceArea
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart3, ScatterChartIcon, Activity } from "lucide-react";

const ChartsPanel = forwardRef(function ChartsPanel({ histograms, scatter, ccf, thresholds }, ref) {
  const [activeTab, setActiveTab] = useState("histogram");
  const [scatterMode, setScatterMode] = useState("standard");
  const [logRatioTol, setLogRatioTol] = useState(0.5);

  const chartsPanelRef = useRef(null);
  const histogramRef = useRef(null);
  const scatterRef = useRef(null);
  const ccfRef = useRef(null);

  // Expose refs to parent via forwardRef
  useImperativeHandle(ref, () => ({
    getChartRefs: () => ({
      histogram: histogramRef,
      scatter: scatterRef,
      ccf: ccfRef,
      panel: chartsPanelRef
    }),
    getActiveTab: () => activeTab,
    setActiveTab: (tab) => setActiveTab(tab),
    getScatterMode: () => scatterMode
  }), [activeTab, scatterMode]);

  return (
    <motion.div
      ref={chartsPanelRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 }}
      className="metric-card"
      data-testid="charts-panel"
    >
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 mb-4">
          <TabsTrigger value="histogram" className="gap-2 text-xs">
            <BarChart3 className="w-3 h-3" />
            Histogram
          </TabsTrigger>
          <TabsTrigger value="scatter" className="gap-2 text-xs">
            <ScatterChartIcon className="w-3 h-3" />
            Scatter
          </TabsTrigger>
          <TabsTrigger value="ccf" className="gap-2 text-xs">
            <Activity className="w-3 h-3" />
            CCF
          </TabsTrigger>
        </TabsList>

        <TabsContent value="histogram" className="mt-0">
          <div ref={histogramRef}>
            <HistogramChart histograms={histograms} thresholds={thresholds} />
          </div>
        </TabsContent>

        <TabsContent value="scatter" className="mt-0 space-y-4">
          <div ref={scatterRef}>
            {/* Scatter Plot Mode Selector */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1 block">Plot Style</Label>
                <Select value={scatterMode} onValueChange={setScatterMode}>
                  <SelectTrigger className="h-8 text-xs" data-testid="scatter-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="paper">Paper Style (Log Ratio)</SelectItem>
                    <SelectItem value="quadrant">Quadrant Analysis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {scatterMode === "paper" && (
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    Log Ratio Tolerance: {logRatioTol.toFixed(1)}
                  </Label>
                  <Slider
                    value={[logRatioTol]}
                    onValueChange={([v]) => setLogRatioTol(v)}
                    min={0.1}
                    max={2}
                    step={0.1}
                    className="mt-1"
                    data-testid="log-ratio-slider"
                  />
                </div>
              )}
            </div>
            
            {scatterMode === "standard" && (
              <ScatterPlotStandard scatter={scatter} thresholds={thresholds} />
            )}
            {scatterMode === "paper" && (
              <ScatterPlotPaper scatter={scatter} logRatioTol={logRatioTol} />
            )}
            {scatterMode === "quadrant" && (
              <ScatterPlotQuadrant scatter={scatter} thresholds={thresholds} />
            )}
          </div>
        </TabsContent>

        <TabsContent value="ccf" className="mt-0">
          <div ref={ccfRef}>
            <CCFChart ccf={ccf} />
          </div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
});

function HistogramChart({ histograms, thresholds }) {
  if (!histograms?.red?.values || !histograms?.green?.values) {
    return <EmptyState message="No histogram data available" />;
  }

  const maxLength = Math.max(
    histograms.red.values.length,
    histograms.green.values.length
  );
  
  const data = [];
  for (let i = 0; i < maxLength; i++) {
    data.push({
      bin: histograms.red.bins?.[i]?.toFixed(2) || i,
      red: histograms.red.values[i] || 0,
      green: histograms.green.values[i] || 0
    });
  }

  return (
    <div className="h-80" data-testid="histogram-chart">
      <ResponsiveContainer width="100%" height="85%">
        <BarChart data={data} barGap={0} margin={{ bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
          <XAxis 
            dataKey="bin" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            tickFormatter={(v) => parseFloat(v).toFixed(1)}
            label={{ value: "Intensity", position: "bottom", fill: "#64748b", fontSize: 11, dy: 10 }}
          />
          <YAxis 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            label={{ value: "Count", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(15, 23, 42, 0.9)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px"
            }}
          />
          <Bar dataKey="red" fill="#ef4444" opacity={0.7} />
          <Bar dataKey="green" fill="#22c55e" opacity={0.7} />
        </BarChart>
      </ResponsiveContainer>
      
      <div className="flex items-center justify-center gap-6 mt-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500" />
          <span className="text-xs text-muted-foreground">Red Foreground</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500" />
          <span className="text-xs text-muted-foreground">Green Foreground</span>
        </div>
      </div>
    </div>
  );
}

function ScatterPlotStandard({ scatter, thresholds }) {
  if (!scatter?.red || !scatter?.green) {
    return <EmptyState message="No scatter data available" />;
  }

  const data = scatter.red.map((r, i) => ({
    red: r,
    green: scatter.green[i],
    z: 1
  }));

  return (
    <div className="h-80" data-testid="scatter-chart-standard">
      <ResponsiveContainer width="100%" height="85%">
        <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
          <XAxis 
            type="number" 
            dataKey="red" 
            name="Red" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            domain={[0, 1]}
            label={{ value: "Red Intensity", position: "bottom", fill: "#64748b", fontSize: 11, dy: 15 }}
          />
          <YAxis 
            type="number" 
            dataKey="green" 
            name="Green" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            domain={[0, 1]}
            label={{ value: "Green Intensity", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }}
          />
          <ZAxis type="number" dataKey="z" range={[10, 10]} />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{
              background: "rgba(15, 23, 42, 0.9)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px"
            }}
          />
          {thresholds?.red && (
            <ReferenceLine x={thresholds.red} stroke="#ef4444" strokeDasharray="3 3" />
          )}
          {thresholds?.green && (
            <ReferenceLine y={thresholds.green} stroke="#22c55e" strokeDasharray="3 3" />
          )}
          <Scatter data={data} fill="#fbbf24" opacity={0.6} />
        </ScatterChart>
      </ResponsiveContainer>
      
      <p className="text-xs text-muted-foreground text-center mt-2">
        Standard scatter plot with threshold lines
      </p>
    </div>
  );
}

function ScatterPlotPaper({ scatter, logRatioTol }) {
  const data = useMemo(() => {
    if (!scatter?.red || !scatter?.green) return [];
    
    return scatter.red.map((r, i) => {
      const g = scatter.green[i];
      // Avoid log(0)
      const safeR = Math.max(r, 0.001);
      const safeG = Math.max(g, 0.001);
      const logRatio = Math.log10(safeR / safeG);
      const avgIntensity = (r + g) / 2;
      
      // Classify based on log ratio
      let category = "balanced";
      if (logRatio > logRatioTol) category = "red_dominant";
      else if (logRatio < -logRatioTol) category = "green_dominant";
      
      return {
        logRatio,
        avgIntensity,
        category,
        red: r,
        green: g
      };
    });
  }, [scatter, logRatioTol]);

  if (data.length === 0) {
    return <EmptyState message="No scatter data available" />;
  }

  const colorMap = {
    red_dominant: "#ef4444",
    green_dominant: "#22c55e",
    balanced: "#fbbf24"
  };

  return (
    <div className="h-80" data-testid="scatter-chart-paper">
      <ResponsiveContainer width="100%" height="80%">
        <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 35 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
          <XAxis 
            type="number" 
            dataKey="avgIntensity" 
            name="Avg Intensity" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            domain={[0, 1]}
            label={{ value: "Average Intensity", position: "bottom", fill: "#64748b", fontSize: 11, dy: 15 }}
          />
          <YAxis 
            type="number" 
            dataKey="logRatio" 
            name="Log Ratio" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            domain={[-3, 3]}
            label={{ value: "Log₁₀(Red/Green)", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(15, 23, 42, 0.9)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px"
            }}
            formatter={(value, name) => {
              if (name === "Log Ratio") return value.toFixed(3);
              if (name === "Avg Intensity") return value.toFixed(3);
              return value;
            }}
          />
          {/* Tolerance bands */}
          <ReferenceLine y={logRatioTol} stroke="#ef4444" strokeDasharray="5 5" opacity={0.5} />
          <ReferenceLine y={-logRatioTol} stroke="#22c55e" strokeDasharray="5 5" opacity={0.5} />
          <ReferenceLine y={0} stroke="#fbbf24" strokeDasharray="3 3" />
          
          <Scatter data={data} opacity={0.7}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colorMap[entry.category]} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      
      <div className="flex items-center justify-center gap-4 mt-3">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-xs text-muted-foreground">Red dominant</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span className="text-xs text-muted-foreground">Balanced</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs text-muted-foreground">Green dominant</span>
        </div>
      </div>
    </div>
  );
}

function ScatterPlotQuadrant({ scatter, thresholds }) {
  const { data, stats } = useMemo(() => {
    if (!scatter?.red || !scatter?.green || !thresholds) {
      return { data: [], stats: null };
    }
    
    const thrR = thresholds.red || 0.5;
    const thrG = thresholds.green || 0.5;
    
    let q1 = 0, q2 = 0, q3 = 0, q4 = 0;
    
    const points = scatter.red.map((r, i) => {
      const g = scatter.green[i];
      let quadrant;
      
      if (r >= thrR && g >= thrG) { quadrant = "Q1"; q1++; }
      else if (r < thrR && g >= thrG) { quadrant = "Q2"; q2++; }
      else if (r < thrR && g < thrG) { quadrant = "Q3"; q3++; }
      else { quadrant = "Q4"; q4++; }
      
      return { red: r, green: g, quadrant };
    });
    
    const total = points.length;
    return {
      data: points,
      stats: {
        q1: ((q1 / total) * 100).toFixed(1),
        q2: ((q2 / total) * 100).toFixed(1),
        q3: ((q3 / total) * 100).toFixed(1),
        q4: ((q4 / total) * 100).toFixed(1)
      }
    };
  }, [scatter, thresholds]);

  if (data.length === 0) {
    return <EmptyState message="No scatter data available" />;
  }

  const quadrantColors = {
    Q1: "#fbbf24", // Both high - colocalized
    Q2: "#22c55e", // Green high, Red low
    Q3: "#64748b", // Both low - background
    Q4: "#ef4444"  // Red high, Green low
  };

  const thrR = thresholds?.red || 0.5;
  const thrG = thresholds?.green || 0.5;

  return (
    <div className="h-80" data-testid="scatter-chart-quadrant">
      <ResponsiveContainer width="100%" height="75%">
        <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 35 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
          <XAxis 
            type="number" 
            dataKey="red" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            domain={[0, 1]}
            label={{ value: "Red Intensity", position: "bottom", fill: "#64748b", fontSize: 11, dy: 15 }}
          />
          <YAxis 
            type="number" 
            dataKey="green" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            domain={[0, 1]}
            label={{ value: "Green Intensity", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(15, 23, 42, 0.9)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px"
            }}
          />
          
          {/* Quadrant shading */}
          <ReferenceArea x1={thrR} x2={1} y1={thrG} y2={1} fill="#fbbf24" fillOpacity={0.1} />
          <ReferenceArea x1={0} x2={thrR} y1={thrG} y2={1} fill="#22c55e" fillOpacity={0.1} />
          <ReferenceArea x1={0} x2={thrR} y1={0} y2={thrG} fill="#64748b" fillOpacity={0.1} />
          <ReferenceArea x1={thrR} x2={1} y1={0} y2={thrG} fill="#ef4444" fillOpacity={0.1} />
          
          {/* Threshold lines */}
          <ReferenceLine x={thrR} stroke="#ef4444" strokeWidth={2} />
          <ReferenceLine y={thrG} stroke="#22c55e" strokeWidth={2} />
          
          <Scatter data={data} opacity={0.6}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={quadrantColors[entry.quadrant]} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      
      {stats && (
        <div className="grid grid-cols-4 gap-2 mt-3 text-center">
          <div>
            <div className="text-xs text-muted-foreground">Q1 (Both+)</div>
            <div className="font-mono text-sm text-yellow-400">{stats.q1}%</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Q2 (G+R-)</div>
            <div className="font-mono text-sm text-green-400">{stats.q2}%</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Q3 (Both-)</div>
            <div className="font-mono text-sm text-slate-400">{stats.q3}%</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Q4 (R+G-)</div>
            <div className="font-mono text-sm text-red-400">{stats.q4}%</div>
          </div>
        </div>
      )}
    </div>
  );
}

function CCFChart({ ccf }) {
  if (!ccf?.shifts || !ccf?.correlations) {
    return <EmptyState message="No CCF data available" />;
  }

  const data = ccf.shifts.map((shift, i) => ({
    shift,
    correlation: ccf.correlations[i]
  }));

  return (
    <div className="h-80" data-testid="ccf-chart">
      <ResponsiveContainer width="100%" height="75%">
        <LineChart data={data} margin={{ bottom: 25, left: 10, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
          <XAxis 
            dataKey="shift" 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            label={{ value: "Pixel Shift", position: "bottom", fill: "#64748b", fontSize: 11, dy: 15 }}
          />
          <YAxis 
            stroke="#64748b"
            tick={{ fill: "#64748b", fontSize: 10 }}
            label={{ value: "Correlation", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(15, 23, 42, 0.9)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px"
            }}
          />
          <ReferenceLine x={0} stroke="#64748b" strokeDasharray="3 3" />
          <ReferenceLine x={ccf.best_shift} stroke="#3b82f6" strokeWidth={2} />
          <Line
            type="monotone"
            dataKey="correlation"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
      
      <div className="flex items-center justify-center gap-4 mt-2">
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Best Shift</p>
          <p className="font-mono text-sm text-blue-400">{ccf.best_shift} px</p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Peak Correlation</p>
          <p className="font-mono text-sm text-blue-400">{ccf.best_correlation.toFixed(4)}</p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="h-64 flex items-center justify-center">
      <p className="text-muted-foreground text-sm">{message}</p>
    </div>
  );
}

export default ChartsPanel;
